<template>
  <div class="flex flex-col gap-6">
    <!-- Page Header -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-2xl font-bold text-slate-800">申报书格式审查</h1>
        <p class="text-sm text-slate-500 mt-1">对科研项目申报书进行格式规范性审查，包括字体字号、段落缩进、图表格式等</p>
      </div>
      <div class="flex items-center gap-3">
        <span class="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-xs font-semibold">申报初审</span>
        <span class="px-3 py-1.5 bg-amber-50 text-amber-600 rounded-full text-xs font-semibold">建议修改</span>
      </div>
    </div>

    <!-- Main Content Card -->
    <div class="glass-card rounded-3xl p-6">
      <!-- Section 1: Check Items -->
      <div class="mb-8">
        <div class="flex items-center gap-2 mb-4">
          <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
            <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h7"/>
            </svg>
          </div>
          <h2 class="text-lg font-bold text-slate-800">审查项配置</h2>
        </div>
        <p class="text-sm text-slate-500 mb-4 ml-10">选择需要审查的格式项目，系统将对申报书进行多维度格式审查</p>

        <div class="ml-10 space-y-3">
          <label class="flex items-center gap-4 p-4 rounded-xl cursor-pointer border-2 transition-all duration-300" 
                 :class="selectedChecks.includes('font') ? 'border-blue-500 bg-blue-50/30' : 'border-slate-200 bg-white hover:border-blue-300'"
                 @click="toggleCheck('font')">
            <div class="w-10 h-10 rounded-xl flex items-center justify-center transition-colors"
                 :class="selectedChecks.includes('font') ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-500'">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"/>
              </svg>
            </div>
            <div class="flex-1">
              <div class="flex items-center gap-2">
                <h3 class="text-sm font-semibold transition-colors" 
                    :class="selectedChecks.includes('font') ? 'text-blue-700' : 'text-slate-700'">字体字号审查</h3>
                <div class="w-2 h-2 rounded-full transition-colors"
                     :class="selectedChecks.includes('font') ? 'bg-emerald-500' : 'bg-slate-300'"></div>
              </div>
              <p class="text-xs text-slate-500 mt-1">检查申报书各部分字体、字号是否符合规定要求</p>
            </div>
          </label>

          <label class="flex items-center gap-4 p-4 rounded-xl cursor-pointer border-2 transition-all duration-300" 
                 :class="selectedChecks.includes('paragraph') ? 'border-blue-500 bg-blue-50/30' : 'border-slate-200 bg-white hover:border-blue-300'"
                 @click="toggleCheck('paragraph')">
            <div class="w-10 h-10 rounded-xl flex items-center justify-center transition-colors"
                 :class="selectedChecks.includes('paragraph') ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-500'">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z"/>
              </svg>
            </div>
            <div class="flex-1">
              <div class="flex items-center gap-2">
                <h3 class="text-sm font-semibold transition-colors" 
                    :class="selectedChecks.includes('paragraph') ? 'text-blue-700' : 'text-slate-700'">段落缩进审查</h3>
                <div class="w-2 h-2 rounded-full transition-colors"
                     :class="selectedChecks.includes('paragraph') ? 'bg-emerald-500' : 'bg-slate-300'"></div>
              </div>
              <p class="text-xs text-slate-500 mt-1">检查段落缩进、行间距、对齐方式是否符合规范</p>
            </div>
          </label>

          <label class="flex items-center gap-4 p-4 rounded-xl cursor-pointer border-2 transition-all duration-300" 
                 :class="selectedChecks.includes('chart') ? 'border-blue-500 bg-blue-50/30' : 'border-slate-200 bg-white hover:border-blue-300'"
                 @click="toggleCheck('chart')">
            <div class="w-10 h-10 rounded-xl flex items-center justify-center transition-colors"
                 :class="selectedChecks.includes('chart') ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-500'">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
              </svg>
            </div>
            <div class="flex-1">
              <div class="flex items-center gap-2">
                <h3 class="text-sm font-semibold transition-colors" 
                    :class="selectedChecks.includes('chart') ? 'text-blue-700' : 'text-slate-700'">图表格式审查</h3>
                <div class="w-2 h-2 rounded-full transition-colors"
                     :class="selectedChecks.includes('chart') ? 'bg-emerald-500' : 'bg-slate-300'"></div>
              </div>
              <p class="text-xs text-slate-500 mt-1">检查图表标题、编号、图例、数据标签等格式是否规范</p>
            </div>
          </label>
        </div>
      </div>

      <!-- Section 2: Input -->
      <div class="mb-8">
        <div class="flex items-center gap-2 mb-4">
          <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
            <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
          </div>
          <h2 class="text-lg font-bold text-slate-800">申报书信息输入</h2>
        </div>
        <p class="text-sm text-slate-500 mb-4 ml-10">输入申报书内容或上传文件，进行格式审查分析</p>

        <div class="ml-10">
          <div class="relative">
            <textarea
              v-model="declarationContent"
              class="premium-input w-full h-40 rounded-2xl p-5 text-sm leading-relaxed resize-none"
              placeholder="请粘贴申报书内容..."
            ></textarea>
            <div class="absolute bottom-4 right-4 text-xs text-slate-400">
              {{ declarationContent.length }} 字符
            </div>
          </div>

          <div class="mt-4 flex items-center gap-4">
            <button class="flex items-center gap-2 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 rounded-xl text-sm font-medium text-slate-600 transition-colors">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/>
              </svg>
              上传文件
            </button>
            <span class="text-xs text-slate-400">支持 PDF、DOCX 格式</span>
          </div>
        </div>
      </div>

      <!-- Action Button -->
      <div class="flex justify-center">
        <button class="btn-action px-10 py-4 rounded-2xl text-white font-bold text-base flex items-center gap-3">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h7"/>
          </svg>
          启动格式审查
        </button>
      </div>
    </div>

    <!-- Check Results -->
    <div class="glass-card rounded-3xl p-6">
      <div class="flex items-center justify-between mb-6">
        <div class="flex items-center gap-2">
          <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
            <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
            </svg>
          </div>
          <h2 class="text-lg font-bold text-slate-800">审查结果</h2>
        </div>
        <button class="btn-export px-4 py-2 rounded-xl text-white text-sm font-medium flex items-center gap-2">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
          </svg>
          导出报告
        </button>
      </div>

      <div class="space-y-4">
        <!-- Format Check Result - No Issues -->
        <div class="p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
          <div class="flex items-center gap-3">
            <div class="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center">
              <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
              </svg>
            </div>
            <div class="flex-1">
              <h4 class="font-semibold text-emerald-800 text-sm">申报书格式问题：无问题</h4>
              <p class="text-xs text-emerald-600 mt-1">申报书整体格式符合规范要求</p>
            </div>
          </div>
        </div>

        <!-- Format Check Result - Suggestion -->
        <div class="p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <div class="flex items-center gap-3">
            <div class="w-8 h-8 rounded-lg bg-amber-500 flex items-center justify-center">
              <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
              </svg>
            </div>
            <div class="flex-1">
              <h4 class="font-semibold text-amber-800 text-sm">提示：建议完善</h4>
              <p class="text-xs text-amber-600 mt-1">虽然格式审查整体无问题，但建议完善部分图表的标题格式，使其更加规范统一</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DeclareFormatCheck',
  data() {
    return {
      selectedChecks: ['font', 'paragraph', 'chart'],
      declarationContent: ''
    }
  },
  methods: {
    toggleCheck(check) {
      const index = this.selectedChecks.indexOf(check)
      if (index > -1) {
        this.selectedChecks.splice(index, 1)
      } else {
        this.selectedChecks.push(check)
      }
    }
  }
}
</script>